import React, { useEffect, useMemo, useState } from 'react';
import './AdminColumbarium.css';

const API_BASE_URL = process.env.REACT_APP_SHEETS_API_BASE_URL || 'http://localhost:8787';

const SAMPLE_RECORDS = [
  {
    id: 'sample-a1-1',
    area: 'A',
    section: 'A1',
    slotNumber: 'A1-01',
    status: '안치 완료',
    deceasedName: '밀양박씨문중묘',
    contractName: '박○○',
    phone: '010-0000-0000',
    contractDate: '2026-01-10',
    maintenanceStatus: '정상',
    memo: '샘플 데이터입니다.',
    mapKey: 'A1'
  },
  {
    id: 'sample-c3-1',
    area: 'C',
    section: 'C3',
    slotNumber: 'C3-12',
    status: '예약',
    deceasedName: '김○○',
    contractName: '김○○',
    phone: '010-1111-1111',
    contractDate: '2026-03-02',
    maintenanceStatus: '확인 필요',
    memo: '구글시트 연결 전 표시용 샘플입니다.',
    mapKey: 'C3'
  }
];

const MAP_AREAS = [
  { key: 'A1', label: 'A1', className: 'map-a1' },
  { key: 'A2', label: 'A2', className: 'map-a2' },
  { key: 'A3', label: 'A3', className: 'map-a3' },
  { key: 'A4', label: 'A4', className: 'map-a4' },
  { key: 'A5', label: 'A5', className: 'map-a5' },
  { key: 'A6', label: 'A6', className: 'map-a6' },
  { key: 'B', label: 'B', className: 'map-b' },
  { key: 'C1', label: 'C1', className: 'map-c1' },
  { key: 'C2', label: 'C2', className: 'map-c2' },
  { key: 'C3', label: 'C3', className: 'map-c3' },
  { key: 'C4', label: 'C4', className: 'map-c4' },
  { key: 'C5', label: 'C5', className: 'map-c5' },
  { key: 'C6', label: 'C6', className: 'map-c6' },
  { key: 'D1', label: 'D1', className: 'map-d1' },
  { key: 'D2', label: 'D2', className: 'map-d2' },
  { key: 'D3', label: 'D3', className: 'map-d3' },
  { key: 'D4', label: 'D4', className: 'map-d4' },
  { key: 'D5', label: 'D5', className: 'map-d5' },
  { key: 'D6', label: 'D6', className: 'map-d6' },
  { key: 'E1', label: 'E1', className: 'map-e1' },
  { key: 'E2', label: 'E2', className: 'map-e2' },
  { key: 'E3', label: 'E3', className: 'map-e3' },
  { key: 'E4', label: 'E4', className: 'map-e4' },
  { key: 'E5', label: 'E5', className: 'map-e5' },
  { key: 'E6', label: 'E6', className: 'map-e6' }
];

function normalize(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, '');
}

function recordMatches(record, query) {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return true;
  }
  return normalize(Object.values(record).join(' ')).includes(normalizedQuery);
}

function getRecordTitle(record) {
  return record.deceasedName || record.contractName || record.slotNumber || '이름 없음';
}

function AdminColumbarium() {
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedMapKey, setSelectedMapKey] = useState('');
  const [selectedRecordId, setSelectedRecordId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapImageReady, setMapImageReady] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    async function loadRecords() {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/columbarium/records`, {
          signal: controller.signal
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.message || '구글시트 데이터를 읽지 못했습니다.');
        }
        setRecords(payload.records || []);
      } catch (loadError) {
        if (loadError.name !== 'AbortError') {
          setError(loadError.message);
          setRecords(SAMPLE_RECORDS);
        }
      } finally {
        setLoading(false);
      }
    }

    loadRecords();
    return () => controller.abort();
  }, []);

  const filteredRecords = useMemo(() => (
    records.filter(record => recordMatches(record, query))
  ), [records, query]);

  const selectedRecord = useMemo(() => (
    filteredRecords.find(record => record.id === selectedRecordId) || filteredRecords[0] || null
  ), [filteredRecords, selectedRecordId]);

  const activeMapKey = selectedMapKey || selectedRecord?.mapKey || '';

  const recordsByMapKey = useMemo(() => (
    records.reduce((groups, record) => {
      const key = record.mapKey || record.area || '미지정';
      groups[key] = (groups[key] || 0) + 1;
      return groups;
    }, {})
  ), [records]);

  function handleSelectMap(key) {
    setSelectedMapKey(key);
    const firstRecord = records.find(record => record.mapKey === key || record.area === key);
    setSelectedRecordId(firstRecord?.id || '');
  }

  return (
    <main className="admin-columbarium">
      <section className="admin-hero">
        <p className="admin-eyebrow">직원용 관리 화면</p>
        <h1>아산추모공원 안치 관리 시스템</h1>
        <p>구글시트 원본 데이터를 읽어 고인명, 계약자, 칸 번호를 검색하고 지도 위치를 확인합니다.</p>
        <div className="admin-search">
          <input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="고인명, 계약자명, 연락처, 칸 번호를 검색하세요"
          />
          <span>{filteredRecords.length}건</span>
        </div>
      </section>

      {error && (
        <section className="admin-alert">
          <strong>구글시트 연결 대기 중</strong>
          <span>{error}</span>
          <small>서비스 계정 키를 설정하면 실제 시트 데이터로 전환됩니다.</small>
        </section>
      )}

      <section className="admin-grid">
        <div className="admin-card map-card">
          <div className="card-heading">
            <h2>전체 위치도</h2>
            <span>{loading ? '불러오는 중' : '구역을 클릭하세요'}</span>
          </div>
          <div className={`map-stage ${mapImageReady ? '' : 'map-stage-fallback'}`}>
            {mapImageReady ? (
              <img
                src="/columbarium-map.png"
                alt="아산추모공원 전체 봉안당 위치도"
                onError={() => setMapImageReady(false)}
              />
            ) : (
              <div className="map-placeholder">
                <strong>위치도 이미지 대기 중</strong>
                <span>public/columbarium-map.png 파일을 추가하면 실제 이미지가 표시됩니다.</span>
              </div>
            )}
            {MAP_AREAS.map(area => (
              <button
                key={area.key}
                type="button"
                className={`map-hotspot ${area.className} ${activeMapKey === area.key ? 'active' : ''}`}
                onClick={() => handleSelectMap(area.key)}
              >
                <span>{area.label}</span>
                <small>{recordsByMapKey[area.key] || 0}</small>
              </button>
            ))}
          </div>
        </div>

        <aside className="admin-card detail-card">
          <div className="card-heading">
            <h2>상세 정보</h2>
            <span>{selectedRecord ? selectedRecord.mapKey || selectedRecord.area : '선택 없음'}</span>
          </div>
          {selectedRecord ? (
            <dl>
              <dt>고인명</dt><dd>{getRecordTitle(selectedRecord)}</dd>
              <dt>계약자</dt><dd>{selectedRecord.contractName || '-'}</dd>
              <dt>연락처</dt><dd>{selectedRecord.phone || '-'}</dd>
              <dt>칸 번호</dt><dd>{selectedRecord.slotNumber || '-'}</dd>
              <dt>상태</dt><dd>{selectedRecord.status || '-'}</dd>
              <dt>관리비</dt><dd>{selectedRecord.maintenanceStatus || '-'}</dd>
              <dt>계약/안치일</dt><dd>{selectedRecord.contractDate || '-'}</dd>
              <dt>원본 위치</dt><dd>{selectedRecord.sourceSheet ? `${selectedRecord.sourceSheet} ${selectedRecord.sourceRow}행` : '샘플'}</dd>
              <dt>메모</dt><dd>{selectedRecord.memo || '-'}</dd>
            </dl>
          ) : (
            <p className="empty-state">검색 결과가 없습니다.</p>
          )}
        </aside>
      </section>

      <section className="admin-card records-card">
        <div className="card-heading">
          <h2>검색 결과</h2>
          <span>최대 300건 표시</span>
        </div>
        <div className="record-list">
          {filteredRecords.map(record => (
            <button
              type="button"
              key={record.id}
              className={`record-row ${selectedRecord?.id === record.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedRecordId(record.id);
                setSelectedMapKey(record.mapKey || record.area || '');
              }}
            >
              <strong>{getRecordTitle(record)}</strong>
              <span>{record.mapKey || record.area || '위치 미지정'}</span>
              <span>{record.slotNumber || '-'}</span>
              <span>{record.contractName || '-'}</span>
              <span>{record.phone || '-'}</span>
              <em>{record.status || '-'}</em>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

export default AdminColumbarium;
